import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { ActivatedRoute } from '@angular/router'
import {
  ElderHeaderComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSinglePaneWrapperComponent,
  ElderToolbarColumnDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { EntityLinkComponent } from '../../../entitylinks/entity-link/entity-link.component'
import { WorkOrder } from '../work-order'
import { WorkOrderFullComponent } from '../work-order-full/work-order-full.component'
import { WorkOrderService } from '../work-order.service'

class WorkOrderContext {
  public static order(order: WorkOrder): WorkOrderContext {
    return new WorkOrderContext(false, order.id, order, null)
  }

  public static error(orderNumber: string, err: string): WorkOrderContext {
    return new WorkOrderContext(false, orderNumber, null, err)
  }

  public static loading(orderNumber: string): WorkOrderContext {
    return new WorkOrderContext(true, orderNumber, null, null)
  }

  constructor(
    public readonly loading: boolean,
    public readonly orderNumber: string,
    public readonly order: WorkOrder | null,
    public readonly error: string | null
  ) {}
}

@Component({
  selector: 'app-work-order-detail-full',
  templateUrl: './work-order-detail-full.component.html',
  styleUrls: ['./work-order-detail-full.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneContentComponent,
    ElderToolbarColumnDirective,
    ElderHeaderComponent,
    EntityLinkComponent,
    WorkOrderFullComponent,
    MatIcon,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkOrderDetailFullComponent implements OnInit {
  private route = inject(ActivatedRoute)
  private workOrderService = inject(WorkOrderService)

  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly logger = LoggerFactory.getLogger(this.constructor.name)

  private readonly orderId$ = new BehaviorSubject<string>(null)

  public currentTabIndex: number
  public readonly orderContext$ = new BehaviorSubject<WorkOrderContext>(
    WorkOrderContext.loading('')
  )

  /***************************************************************************
   *                                                                         *
   * Lifecycle                                                               *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (this.orderId) {
      this.reloadOrder(this.orderId)
    } else {
      this.reloadOrder(this.route.snapshot.params['orderId'])
    }
    this.currentTabIndex = 0
  }

  /***************************************************************************
   *                                                                         *
   * Getters / Setters                                                       *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set orderId(id: string) {
    this.orderId$.next(id)
  }

  public get orderId(): string {
    return this.orderId$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private reloadOrder(orderId: string): void {
    this.orderContext$.next(WorkOrderContext.loading(orderId))

    this.workOrderService.findById(orderId).subscribe({
      next: (order) => {
        this.logger.info('Successfully loaded order: ', order)
        this.orderContext$.next(WorkOrderContext.order(order))
      },
      error: (err) => {
        this.logger.error('Failed to load order ' + orderId, err)
        this.orderContext$.next(WorkOrderContext.error(orderId, err.error.message))
      },
    })
  }
}
